import { Eye, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import PopOver from '@/elements/PopOver'
import { AlertSources } from '@/api/types/Alert'
import { AlertSourceConfigs, searchAliasInSource } from '@/utils/alertSources'
import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import type { IndividualAliasResponse } from '@/api/types/Organization'

interface IndividualAliasTableType {
  id: string
  name: string
}

const columnHelper = createColumnHelper<IndividualAliasTableType>()

export function useIndividualAliasTable(
  alias: IndividualAliasResponse[],
  onRemoveAlias: (id: string) => void
) {
  const data: IndividualAliasTableType[] = useMemo(() => (
    alias.map(a => ({
      id: a.id,
      name: a.alias,
    }))
  ), [alias])

  const columns: ColumnDef<IndividualAliasTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions,
      header: 'Alias Name',
      enableSorting: false,
      cell: info => (
        <Flex className='user-cell'>
          <Text variant='textblock'>{info.getValue() as string}</Text>
        </Flex>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 75 },
      enableSorting: false,
      cell: info => {
        const aliasId = info.row.original.id
        const aliasName = info.row.original.name
        const canDelete = alias.find(a => a.id === aliasId)?.editable ?? true
        return (
          <Flex inline justify='center' align='center' gap={2} className='actions-cell'>
            <PopOver
              position='left'
              icon={Eye}
              options={[
                {
                  label: AlertSourceConfigs[AlertSources[0]].label,
                  action: () => searchAliasInSource(aliasName, AlertSources[0]),
                },
                {
                  label: AlertSourceConfigs[AlertSources[1]].label,
                  action: () => searchAliasInSource(aliasName, AlertSources[1]),
                },
                {
                  label: AlertSourceConfigs[AlertSources[2]].label,
                  action: () => searchAliasInSource(aliasName, AlertSources[2]),
                }, {
                  label: AlertSourceConfigs[AlertSources[3]].label,
                  action: () => searchAliasInSource(aliasName, AlertSources[3]),
                }
              ]}
            />
            <Button
              small
              variant='iconDangerBtn'
              onClick={() => onRemoveAlias(aliasId)}
              disabled={!canDelete}
            >
              <Trash2 size={18} />
            </Button>
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
